import { useEffect, useState } from 'react';
import type { TimelineInterval } from '@/types/streaming';
import { computeGaps } from '@/utils/camera-stream';
import { MILLISECONDS_TO_SECONDS } from '@/constants/time';
import { getVideoElementFromRef } from '@/utils/video-player';
import type { VideoPlayerEventEmitter } from '../VideoPlayerEventEmitter';

interface UseTimelineGapStateOptions {
    absoluteTimestamp: number | null;
    timeline: { intervals: TimelineInterval[] } | null;
    isLive: boolean;
    isCameraSource: boolean;
    streamBeginTimestamp: number | null;
    videoRef: React.RefObject<HTMLVideoElement | null>;
    emitter: VideoPlayerEventEmitter;
}

export function useTimelineGapState({
    absoluteTimestamp,
    timeline,
    isLive,
    isCameraSource,
    streamBeginTimestamp,
    videoRef,
    emitter,
}: UseTimelineGapStateOptions) {
    const [streamUnavailable, setStreamUnavailable] = useState(false);

    useEffect(() => {
        const updateGapState = () => {
            const video = getVideoElementFromRef(videoRef);
            const timestamp =
                video && streamBeginTimestamp !== null
                    ? streamBeginTimestamp + video.currentTime
                    : absoluteTimestamp;
            if (
                !isCameraSource ||
                isLive ||
                !timeline?.intervals.length ||
                timestamp === null
            ) {
                setStreamUnavailable(false);
                return;
            }
            const inArchiveGap = computeGaps(
                timeline.intervals,
                Date.now() / MILLISECONDS_TO_SECONDS,
            ).some(({ begin, end }) => timestamp > begin && timestamp < end);
            setStreamUnavailable(inArchiveGap);
        };

        updateGapState();
        return emitter.on('timeupdate', updateGapState);
    }, [
        absoluteTimestamp,
        timeline,
        isLive,
        isCameraSource,
        streamBeginTimestamp,
        videoRef,
        emitter,
    ]);

    return { streamUnavailable };
}
